import type { Ref } from 'vue'
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'

import { Tooltip } from '@app/setup/tooltip/uikit-tooltip'
import { SfLog } from '@app/adapters/logger'
import useI18n from '@app/shared/vue/composables/use-I18n'

export interface PulseMentionWarning {
  periodName: string
  users: { username: string; name: string }[]
  keywords?: string[]
}

// Keep in visual sync with `.mention--pulse-warning` in sf-markdown-editor.vue.
// Text colour needs !important to beat the UI kit's `.sf-text strong` rule.
const CHIP_CLASSES = [
  'tw-bg-sf-warning-subtle-2',
  '!tw-text-sf-warning',
  'tw-font-normal',
  'tw-rounded-[5px]',
  'tw-px-[5px]',
  'tw-py-[2px]',
  'tw-box-decoration-clone',
]

const CHIP_TESTID = 'pulse-mention-warning'

/**
 * Decorates `@username` mentions of Pulse users in a v-html-rendered body with
 * an amber chip + tooltip. The body is raw HTML, so the global
 * `data-toggle=tooltip` scanner never sees it — we decorate here in JS.
 * Disposes tooltips on re-run so none are orphaned in document.body.
 */
export default function usePulseMentionWarning(
  container: Ref<HTMLElement | null>,
  warning: () => PulseMentionWarning | null | undefined,
  renderedText: () => string
): void {
  const I18n = useI18n()

  let chips: HTMLElement[] = []
  let icons: HTMLElement[] = []
  let tooltips: Tooltip[] = []

  function clear(): void {
    tooltips.forEach((tooltip) => {
      try {
        tooltip.dispose()
      } catch (error) {
        SfLog.warn('Error disposing pulse mention tooltip', {
          extra: { error },
        })
      }
    })
    tooltips = []
    icons.forEach((icon) => icon.remove())
    icons = []
    chips.forEach((chip) => {
      chip.classList.remove(...CHIP_CLASSES)
      delete chip.dataset.testid
    })
    chips = []
  }

  function decorate(): void {
    clear()

    const value = warning()
    const root = container.value
    if (!value || !root) return

    const nameByMention = new Map(
      value.users.map((user) => [`@${user.username}`, user.name])
    )
    const keywordMentions = new Set(
      (value.keywords ?? []).map((keyword) => `@${keyword}`)
    )

    root.querySelectorAll('strong').forEach((mention) => {
      const text = mention.textContent?.trim() ?? ''
      const name = nameByMention.get(text)
      const isKeyword = keywordMentions.has(text)
      if (!name && !isKeyword) return

      mention.classList.add(...CHIP_CLASSES)
      mention.dataset.testid = CHIP_TESTID
      chips.push(mention)

      const icon = document.createElement('span')
      icon.className = 'fa fa-exclamation-circle tw-mr-1'
      mention.prepend(icon)
      icons.push(icon)

      const title = name
        ? I18n.t('communication.cards.pulse_mention_private_period_tooltip', {
            name,
            period: value.periodName,
          })
        : I18n.t(
            'communication.cards.pulse_mention_private_period_keyword_tooltip',
            { period: value.periodName }
          )

      tooltips.push(
        new Tooltip(mention, { title, placement: 'bottom', size: 'sm' })
      )
    })
  }

  onMounted(() => nextTick(decorate))
  watch([renderedText, warning], () => nextTick(decorate))
  onBeforeUnmount(clear)
}
